import * as echarts from 'echarts/core';
import { PieChart, BarChart, LineChart, GaugeChart, FunnelChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent, GridComponent, TitleComponent, GraphicComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { LegacyGridContainLabel, LabelLayout } from 'echarts/features';
import Logging from '../../utils/logging';

// register the ECharts modules we actually use (keeps the bundle tree-shakable);
// LegacyGridContainLabel keeps grid.containLabel working (opt-in since ECharts 6);
// LabelLayout enables series labelLayout (e.g. hideOverlap for value labels)
echarts.use([ PieChart, BarChart, LineChart, GaugeChart, FunnelChart, TooltipComponent, LegendComponent, GridComponent, TitleComponent, GraphicComponent, CanvasRenderer, LegacyGridContainLabel, LabelLayout ]);

const LOG = Logging.getInstance().getLogger('widgets.jschart.ChartOption');

// the server used to assume every chart is rendered "big" (it never learned the
// actual tile size when it produced a PNG on request); now that JSON is always
// built for one assumed size, it sends two legend flags - "legendPosition" for
// a big tile and "legendPositionSmall" for a small one - and leaves the actual
// size decision to the client. A top/bottom legend eats into the available
// height, a left/right legend into the available width, so the two orientations
// need separate thresholds rather than one combined (e.g. area) measure. A
// maximized chart in a typical layout measures ~1114x472px; ~420px height /
// ~800px width are used as the "big" cutoffs.
// TODO: untuned - revisit against real measurements of actual small vs. big
// tile sizes once available (not yet gathered).
const BIG_CHART_HEIGHT = 420;
const BIG_CHART_WIDTH = 800;

// legend limits (CRM-2017): a chart with many items must not let its legend flood the tile.
// Up to LEGEND_FULL_MAX_ITEMS the legend is shown as is, up to LEGEND_MAX_ITEMS it becomes a
// scrollable legend capped at LEGEND_MAX_SHARE of the tile (height for top/bottom, width for
// left/right). Beyond that - or if the plot would drop below MIN_PLOT_SIZE - it is hidden
// (the tooltips still identify the items).
const LEGEND_FULL_MAX_ITEMS = 14;
const LEGEND_MAX_ITEMS = 50;
const LEGEND_MAX_SHARE = 0.3;
const MIN_PLOT_SIZE = 120;
const LEGEND_ROW_HEIGHT = 22;			// height of one legend row
const LEGEND_MARKER_WIDTH = 25 + 5;	// legend marker width + gap to the text
const LEGEND_PAD = 10;					// right-hand breathing room

/**
 * ChartOption - builds ECharts option objects from the backend chart data.
 * <p>
 * This is pure data processing: it takes the JSON the application server delivers
 * (metaData + chartData) plus the locale and produces an ECharts option object.
 * It has no dependency on the widget, the DOM or RAP - the JsChart widget owns
 * the ECharts instance lifecycle and calls {@link build}.
 */
export default class ChartOption {

	/**
	 * builds the ECharts option object from the backend chart data
	 * @param {Object} chartData the chart data (metaData + chartData)
	 * @param {String} locale the locale to use for number formatting
	 * @param {Number} width the available width in pixels (used for label layout); optional
	 * @param {Number} height the available height in pixels (used to scale the gauge); optional
	 * @returns {Object} the ECharts option, or null if the data could not be interpreted
	 */
	static build(chartData, locale, width, height) {
		const meta = chartData ? chartData.metaData : null;
		if ( !meta ) {
			return null;
		}
		// a missing/empty chartData is a valid "no data" case (the server omits the
		// array and provides a noDataMessage) - render an empty chart with the
		// message rather than failing; _noDataTitle handles the overlay
		const data = Array.isArray(chartData.chartData) ? chartData.chartData : [];
		const loc = locale || 'en';
		const wdt = width > 0 ? width : 0;
		const hgt = height > 0 ? height : 0;
		switch ( meta.elementType ) {
			case 'pieChart':
				return ChartOption._pie(meta, data, loc, wdt, hgt);
			case 'barChart':
			case 'lineChart':
			case 'stackedBarChart':
			case 'lineBarChart':
				return ChartOption._axis(meta, data, loc, wdt, hgt);
			case 'gaugeChart':
				return ChartOption._gauge(meta, data, loc, wdt, hgt);
			case 'dynamicFunnelChart':
				return ChartOption._funnel(meta, data, loc, true, wdt, hgt);
			case 'staticFunnelChart':
				return ChartOption._funnel(meta, data, loc, false, wdt, hgt);
			default:
				LOG.error('Unsupported chart type: ' + meta.elementType);
				return null;
		}
	}

	/**
	 * measures the pixel width of a text using a shared canvas 2D context. Used to
	 * size the funnel label gutters to the longest label.
	 * @param {String} text the text to measure
	 * @param {String} font the CSS font shorthand (e.g. "12px sans-serif")
	 * @returns {Number} the text width in pixels
	 */
	static _textWidth(text, font) {
		let ctx = ChartOption._measureCtx;
		if ( !ctx ) {
			ctx = document.createElement('canvas').getContext('2d');
			ChartOption._measureCtx = ctx;
		}
		ctx.font = font || '12px sans-serif';
		return ctx.measureText(text || '').width;
	}

	/**
	 * maps the backend legend position to an ECharts legend option.
	 * <p>
	 * The server used to decide "small" vs "big" itself, from the tile size it
	 * requested a PNG for; with JSON data it never learns the actual tile size, so
	 * it sends both a "legendPosition" (big) and a "legendPositionSmall" (small)
	 * flag and leaves the size decision - and thus which flag applies - to the
	 * client, based on the actual rendered width/height.
	 * @param {Object} meta the chart meta data
	 * @param {Number} width the available width in pixels; optional
	 * @param {Number} height the available height in pixels; optional
	 * @param {Array} names the legend item labels (series / slice / stage names); optional.
	 * Used to keep a legend with many items from flooding the tile: up to
	 * LEGEND_FULL_MAX_ITEMS it is shown as is, up to LEGEND_MAX_ITEMS it becomes a scrollable
	 * legend capped at LEGEND_MAX_SHARE of the tile, beyond that it is hidden. It is also
	 * hidden if the plot would drop below MIN_PLOT_SIZE.
	 * @returns {Object} the ECharts legend option
	 */
	static _legend(meta, width, height, names) {
		// per the spec, the legend is shown only for one of the four defined
		// positions; a missing, empty or unknown value hides it.
		// a top/bottom legend eats into the available height, a left/right legend
		// into the available width - so "big enough" is judged on the dimension
		// that the big-tile position would actually consume; if it does not clear
		// that threshold (or legendPosition is absent), fall back to the small-tile
		// flag. The two flags are independent - a missing legendPositionSmall means
		// "no legend" for a small tile, it does not fall back to legendPosition.
		const bigPos = meta.legendPosition ? meta.legendPosition.toLowerCase() : '';
		const isVertical = bigPos === 'left' || bigPos === 'right';
		const dim = isVertical ? width : height;
		const threshold = isVertical ? BIG_CHART_WIDTH : BIG_CHART_HEIGHT;
		const isBig = bigPos && dim > 0 && dim > threshold;
		const pos = isBig ? bigPos : (meta.legendPositionSmall ? meta.legendPositionSmall.toLowerCase() : '');
		if ( pos !== 'top' && pos !== 'left' && pos !== 'right' && pos !== 'bottom' ) {
			return { show: false };
		}
		const vertical = pos === 'left' || pos === 'right';
		const legend = vertical ? { show: true, [pos]: 0, orient: 'vertical' } : { show: true, [pos]: 0 };
		if ( !Array.isArray(names) ) {
			return legend;		// item count unknown - no limiting
		}
		const items = names.filter((nam) => nam);
		if ( items.length > LEGEND_MAX_ITEMS ) {
			return { show: false };
		}
		// the space the legend would take: one column for a vertical legend, the wrapped rows for
		// a horizontal one - capped at a share of the tile
		const span = vertical ? width : height;
		const need = vertical ? ChartOption._legendColumnWidth(items) : ChartOption._legendRows(items, width) * LEGEND_ROW_HEIGHT;
		const cap = span > 0 ? span * LEGEND_MAX_SHARE : Infinity;
		let reserve = need;
		if ( items.length > LEGEND_FULL_MAX_ITEMS || need > cap ) {
			// scrollable legend: a single row / a column of limited width, paged by arrows;
			// long labels are truncated (the tooltip shows the full name)
			legend.type = 'scroll';
			if ( vertical ) {
				reserve = Math.min(need, cap);
				legend.textStyle = { width: Math.max(40, Math.floor(reserve - LEGEND_MARKER_WIDTH - LEGEND_PAD)), overflow: 'truncate' };
				legend.top = 0;
				legend.height = height > 0 ? Math.max(LEGEND_ROW_HEIGHT * 2, height - 8) : '90%';
			}
			else {
				reserve = LEGEND_ROW_HEIGHT;
				legend.left = 'center';
				legend.width = width > 0 ? Math.max(100, width - 40) : '90%';
			}
		}
		// the plot must keep a minimum size
		const plot = span - reserve;
		if ( span > 0 && plot < MIN_PLOT_SIZE ) {
			return { show: false };
		}
		return legend;
	}

	/**
	 * builds a compact grid for an axis chart. The ECharts default grid keeps large
	 * fixed margins that squash the plot in a short tile; this reserves only a small
	 * base margin plus room on the edge where the legend sits - and, for a horizontal
	 * legend, as many rows as its items wrap into.
	 * @param {Object} legend the legend option (from {@link _legend})
	 * @param {Array} names the legend item labels (series names); optional
	 * @param {Number} width the available width in pixels; optional
	 * @returns {Object} the ECharts grid option
	 */
	static _grid(legend, names, width) {
		// base margins (px); containLabel expands them to fit the axis labels/ticks
		const grid = { top: 12, right: 12, bottom: 8, left: 12, containLabel: true };
		if ( legend && legend.show ) {
			if ( legend.orient === 'vertical' ) {
				// vertical legend - reserve one column on that side, sized to the
				// longest item label (same measurement approach as the horizontal
				// case below) rather than a flat guess that clips a long series name
				// or over-reserves for short ones
				const side = legend.left != null ? 'left' : 'right';
				// a scrollable legend has a limited column width (truncated labels)
				const limited = legend.textStyle && legend.textStyle.width != null;
				grid[side] += limited ? LEGEND_MARKER_WIDTH + legend.textStyle.width + LEGEND_PAD : ChartOption._legendColumnWidth(names);
			}
			else {
				// horizontal legend (top/bottom) - estimate how many rows the items
				// wrap into so a multi-row legend does not overlap the plot
				// (a scrollable legend is a single row)
				const rows = legend.type === 'scroll' ? 1 : ChartOption._legendRows(names, width);
				const gap = rows * LEGEND_ROW_HEIGHT;
				if ( legend.top != null ) { grid.top += gap; }
				else { grid.bottom += gap; }	// default/bottom
			}
		}
		return grid;
	}

	/**
	 * estimates how many rows a horizontal legend wraps into, from the measured item
	 * widths and the available width.
	 * @param {Array} names the legend item labels
	 * @param {Number} width the available width in pixels
	 * @returns {Number} the estimated number of rows (at least 1)
	 */
	static _legendRows(names, width) {
		if ( !Array.isArray(names) || names.length === 0 || !(width > 0) ) {
			return 1;
		}
		const font = '12px sans-serif';
		const itemGap = 10;		// ECharts default gap between legend items
		const markerW = LEGEND_MARKER_WIDTH;
		let rows = 1;
		let x = 0;
		names.forEach((nam) => {
			const w = markerW + ChartOption._textWidth(nam, font) + itemGap;
			if ( x > 0 && x + w > width ) {
				rows++;
				x = w;
			}
			else {
				x += w;
			}
		} );
		return rows;
	}

	/**
	 * measures the column width a vertical (left/right) legend needs to fit its
	 * longest item label without clipping.
	 * @param {Array} names the legend item labels
	 * @returns {Number} the column width in pixels (a sane minimum if there are no names)
	 */
	static _legendColumnWidth(names) {
		const font = '12px sans-serif';
		const markerW = LEGEND_MARKER_WIDTH;
		const pad = LEGEND_PAD;
		if ( !Array.isArray(names) || names.length === 0 ) {
			return markerW + pad;
		}
		const maxTextW = Math.max(0, ...names.map((nam) => ChartOption._textWidth(nam, font)));
		return markerW + maxTextW + pad;
	}

	/**
	 * builds a centered "no data" title to overlay on an (empty) chart
	 * @param {Object} meta the chart meta data
	 * @param {Array} data the chart data points
	 * @returns {Object} the ECharts title option; empty (no text) when there is data
	 */
	static _noDataTitle(meta, data) {
		if ( Array.isArray(data) && data.length > 0 ) {
			return undefined;
		}
		// the chart (axes/grid) still renders; the message is overlaid centered
		const text = meta.noDataMessage || '';
		return {
			text: text,
			left: 'center',
			top: 'center',
			textStyle: { fontWeight: 'normal', color: '#888' }
		};
	}

	/**
	 * whether the data value should be displayed in the chart, per the spec's
	 * "valueLabel" property. The spec is inconsistent (a single boolean vs. a
	 * per-group array), so both forms are accepted.
	 * @param {Object} meta the chart meta data
	 * @param {Number} grp the group index (for the per-group array form); optional
	 * @returns {Boolean} <b>true</b> if the value label should be shown
	 */
	static _showValueLabel(meta, grp) {
		const vl = (meta.properties || {}).valueLabel;
		if ( Array.isArray(vl) ) {
			return vl[grp || 0] === true;
		}
		return vl === true;
	}

	/**
	 * builds a value formatter from a spec number-format definition (xFormat / yFormat).
	 * The spec's key for the pre-comma style is "type"; real server responses emit
	 * it as "style", so both are accepted.
	 * @param {Object} fmt the number-format definition; may be null/undefined
	 * @param {String} locale the locale to use
	 * @returns {Function} a (value) => string formatter, or null if no definition
	 */
	static _numberFormatter(fmt, locale) {
		if ( !fmt ) {
			return null;
		}
		const type = fmt.type || fmt.style || 'long';
		const options = {};
		// the number of decimal places; the spec's numeric styles are integer
		// (e.g. long = 1,234), so a missing fraction means 0 - otherwise Intl would
		// apply its own default (up to 3 for a plain decimal) and show extra digits
		const fraction = ( typeof fmt.fraction === 'number' ) ? fmt.fraction : 0;
		options.minimumFractionDigits = fraction;
		options.maximumFractionDigits = fraction;
		switch ( type ) {
			case 'short':
				options.useGrouping = false;
				break;
			case 'full':
				options.useGrouping = true;
				options.signDisplay = 'exceptZero';
				break;
			case 'percent':
				// Intl multiplies by 100 internally, matching the spec
				options.style = 'percent';
				options.useGrouping = true;
				break;
			case 'long':
			default:
				options.useGrouping = true;
				break;
		}
		// an explicit unit (e.g. "T€") wins: format a plain grouped number and append
		// the unit; the currency style is used only when no unit is given. NOTE: the
		// server has already applied the "scale" to the values, so it is not applied
		// again here - the "unit" already reflects the scaled magnitude (e.g. "T€").
		const unit = ( typeof fmt.unit === 'string' && fmt.unit.length > 0 ) ? fmt.unit : null;
		if ( !unit && fmt.currency ) {
			options.style = 'currency';
			options.currency = fmt.currency;
		}
		const nf = new Intl.NumberFormat(locale, options);
		return (value) => {
			if ( !Number.isFinite(value) ) {
				return String(value);
			}
			const s = nf.format(value);
			return unit ? s + ' ' + unit : s;
		};
	}

	/**
	 * whether a number-format definition denotes integer values (no fractional
	 * part). Used to keep an axis on integer ticks - e.g. an all-zero data set
	 * must show 0..1 in whole steps, not 0.2/0.4/0.6.
	 * @param {Object} fmt the number-format definition; may be null/undefined
	 * @returns {Boolean} <b>true</b> if the format is integer-valued
	 */
	static _isIntegerFormat(fmt) {
		if ( !fmt ) {
			return false;
		}
		// a "unit" (e.g. "T€") wins over currency, same precedence as in
		// _numberFormatter - it is then a plain grouped number plus the unit, not a
		// currency-styled one
		const hasUnit = typeof fmt.unit === 'string' && fmt.unit.length > 0;
		const type = fmt.type || fmt.style || 'long';
		// percent/currency may carry fractions; only the plain numeric styles with
		// no (or zero) fraction are integer-valued
		if ( type === 'percent' || (!hasUnit && fmt.currency) ) {
			return false;
		}
		return !( typeof fmt.fraction === 'number' && fmt.fraction > 0 );
	}

	/**
	 * whether a value rounds to zero under a format's fraction digits - e.g. 0.456
	 * with 0 fraction digits displays as "0", even though the raw value is not
	 * exactly zero. Used to suppress value labels: a near-zero label sitting on a
	 * near-zero-height bar lands right on the x axis line and looks struck through.
	 * @param {Number} value the raw (already server-scaled) value
	 * @param {Object} fmt the number-format definition; may be null/undefined
	 * @returns {Boolean} <b>true</b> if the formatted value displays as zero
	 */
	static _isZeroValue(value, fmt) {
		const fraction = ( fmt && typeof fmt.fraction === 'number' ) ? fmt.fraction : 0;
		return Math.round(value * Math.pow(10, fraction)) === 0;
	}

	/**
	 * builds the ECharts option for a pie chart
	 * @param {Object} meta the chart meta data
	 * @param {Array} data the chart data points
	 * @param {String} locale the locale to use for number formatting
	 * @param {Number} width the available width in pixels (used for the small/big legend decision); optional
	 * @param {Number} height the available height in pixels (used for the small/big legend decision); optional
	 * @returns {Object} the ECharts pie option
	 */
	static _pie(meta, data, locale, width, height) {
		const props = meta.properties || {};
		const titles = props.groupTitles || [];
		const colors = meta.colors || [];
		const items = data.map((point) => {
			return {
				// the backend does not yet send a stable element id in the chart
				// JSON; wired through already so a future server-side id needs no
				// further client change (see JsChart.js#_onChartClick)
				id: point.id,
				name: titles[point.group] || '',
				value: point.value,
				itemStyle: point.color != null && colors[point.color] ? { color: colors[point.color] } : undefined
			};
		} );
		const fmtVal = ChartOption._numberFormatter(props.yFormat, locale);
		return {
			title: ChartOption._noDataTitle(meta, data),
			tooltip: { trigger: 'item', confine: true, valueFormatter: fmtVal || undefined },
			legend: ChartOption._legend(meta, width, height, items.map((it) => it.name)),
			series: [ {
				type: 'pie',
				// drop slice labels that would overlap a neighbour (many small slices); the
				// exact value stays in the tooltip
				labelLayout: { hideOverlap: true },
				data: items
			} ]
		};
	}

	/**
	 * builds the ECharts option for an axis based chart (bar, line, stacked bar, mixed line/bar)
	 * @param {Object} meta the chart meta data
	 * @param {Array} data the chart data points
	 * @param {String} locale the locale to use for number formatting
	 * @param {Number} width the available width in pixels (used to estimate legend rows and for the small/big legend decision)
	 * @param {Number} height the available height in pixels (used for the small/big legend decision); optional
	 * @returns {Object} the ECharts option
	 */
	static _axis(meta, data, locale, width, height) {
		const props = meta.properties || {};
		const colors = meta.colors || [];
		const categories = props.xTitles || [];
		const groupTitles = props.groupTitles || [];
		const groupLine = props.groupLine || [];
		const stacked = meta.elementType === 'stackedBarChart';
		// the value (y) axis carries numbers; format them per the spec's yFormat
		const fmtVal = ChartOption._numberFormatter(props.yFormat, locale);
		// an integer format must keep the axis on whole-number ticks; otherwise an
		// all-zero data set would auto-scale to 0/0.2/0.4/.../1 fractional steps
		const intAxis = ChartOption._isIntegerFormat(props.yFormat);
		// group the data points by their group index; each group becomes one series
		const byGroup = new Map();
		data.forEach((point) => {
			const grp = point.group || 0;
			if ( !byGroup.has(grp) ) {
				byGroup.set(grp, []);
			}
			byGroup.get(grp).push(point);
		} );
		const series = [];
		Array.from(byGroup.keys()).sort((a, b) => a - b).forEach((grp) => {
			// place each point's value at its x index within the category axis;
			// wrapped as {value, id} rather than a plain number so a future
			// server-side element id (not sent today) needs no further client
			// change - see JsChart.js#_onChartClick
			const values = new Array(categories.length).fill(null);
			const points = byGroup.get(grp);
			points.forEach((point) => {
				const x = point.x || 0;
				const item = { value: point.value, id: point.id };
				if ( x < values.length ) {
					values[x] = item;
				}
				else {
					values.push(item);
				}
			} );
			const isLine = groupLine[grp] === true;
			// per the spec a data point's "color" is the index into the palette;
			// a series shares one color, so take it from the point (fall back to
			// the group index when the point carries no color)
			const clrIdx = points[0].color != null ? points[0].color : grp;
			series.push( {
				name: groupTitles[grp] || '',
				type: isLine ? 'line' : 'bar',
				stack: stacked && !isLine ? 'total' : undefined,
				itemStyle: colors[clrIdx] ? { color: colors[clrIdx] } : undefined,
				// suppress labels that display as zero (exact zero, or a near-zero
				// value that rounds to zero under yFormat's fraction digits) - such a
				// label sits on a near-zero-height bar, right on the x axis line, and
				// looks struck through; format the rest per yFormat
				label: { show: ChartOption._showValueLabel(meta, grp), formatter: (p) => ChartOption._isZeroValue(p.value, props.yFormat) ? '' : (fmtVal ? fmtVal(p.value) : p.value) },
				// drop value labels that would overlap a neighbour (e.g. adjacent
				// bars of similar height); the exact value stays in the tooltip
				labelLayout: { hideOverlap: true },
				data: values
			} );
		} );
		// the legend items are the series names (the group titles)
		const legendNames = series.map((s) => s.name || '');
		const legend = ChartOption._legend(meta, width, height, legendNames);
		return {
			title: ChartOption._noDataTitle(meta, data),
			// confine keeps the tooltip inside the (small) widget box instead of
			// overflowing and being clipped
			// trigger: with a proper category axis one tooltip per category lists all its series
			// (axis); with at most one category - e.g. the server sends one series per bar and no
			// xTitles - an axis tooltip has nothing to anchor to (or would list every series), so
			// show the hovered bar's own series name + value instead (item), CRM-2067
			tooltip: { trigger: categories.length > 1 ? 'axis' : 'item', confine: true, valueFormatter: fmtVal || undefined },
			legend: legend,
			// a compact grid (containLabel keeps axis labels in view) - the ECharts
			// default reserves large fixed margins that squash the plot to a sliver
			// in a short cockpit tile; reserve room only for a shown legend (and for
			// as many rows as it wraps into)
			grid: ChartOption._grid(legend, legendNames, width),
			xAxis: { type: 'category', data: categories },
			yAxis: { type: 'value', minInterval: intAxis ? 1 : undefined, axisLabel: fmtVal ? { formatter: fmtVal } : undefined },
			series: series
		};
	}

	/**
	 * builds the ECharts option for a gauge chart
	 * @param {Object} meta the chart meta data
	 * @param {Array} data the chart data points
	 * @param {String} locale the locale to use for number formatting
	 * @param {Number} width the available width in pixels (to scale the fonts)
	 * @param {Number} height the available height in pixels (to scale the fonts)
	 * @returns {Object} the ECharts gauge option, or null if the mandatory needle value is missing
	 */
	static _gauge(meta, data, locale, width, height) {
		const colors = meta.colors || [];
		const needle = data.find((point) => point.type === 'needle');
		if ( !needle || !Number.isFinite(needle.value) ) {
			LOG.error('Gauge chart is missing the needle value.');
			return null;
		}
		const minData = data.find((point) => point.type === 'min');
		const min = (minData && Number.isFinite(minData.value)) ? minData.value : 0;
		const intervals = data.filter((point) => point.type === 'interval');
		// cumulative interval limits -> ECharts axisLine color stops [ratio, color];
		// an interval with a non-numeric value would otherwise poison every
		// subsequent cumulative limit with NaN
		let limit = min;
		const arcs = intervals.filter((point) => Number.isFinite(point.value)).map((point) => {
			limit += point.value;
			return { limit: limit, color: colors[point.color != null ? point.color : 0] || '#ccc' };
		} );
		const maxData = data.find((point) => point.type === 'max');
		// a "max" point with a non-numeric value must fall through to the arcs/
		// needle fallback, not poison the axis with NaN
		const maxVal = (maxData && Number.isFinite(maxData.value)) ? maxData.value : null;
		let max = maxVal != null ? maxVal : (arcs.length > 0 ? arcs[arcs.length - 1].limit : needle.value);
		if ( !Number.isFinite(max) || max <= min ) {
			max = min + 100;
		}
		const span = max - min;
		const colorStops = arcs.map((arc) => [ (arc.limit - min) / span, arc.color ] );
		if ( colorStops.length === 0 || colorStops[colorStops.length - 1][0] < 1 ) {
			// ECharts requires the last stop to reach 1
			colorStops.push( [ 1, '#ccc' ] );
		}
		// format the value display per yFormat (locale-aware); without it ECharts
		// prints the raw number (e.g. all fractional digits, dot as decimal point)
		const props = meta.properties || {};
		const fmtVal = ChartOption._numberFormatter(props.yFormat, locale);
		// an optional literal "units" suffix (the server property name is kept) is
		// appended verbatim to the value display - e.g. " (163%)" after "490 T€"
		const units = typeof props.units === 'string' ? props.units : '';
		// scale the fonts to the gauge size (bounded by the smaller dimension) so
		// they do not dominate / collide on a small tile; clamp to sane ranges
		const ref = Math.min(width || 300, height || 300);
		const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(v)));
		const detailFsz = clamp(ref * 0.06, 9, 16);	// the value display
		const labelFsz = clamp(ref * 0.045, 7, 12);	// the axis tick numbers
		return {
			tooltip: { trigger: 'item', confine: true },
			series: [ {
				type: 'gauge',
				min: min,
				max: max,
				// pull the dial up and enlarge it to use the tile height; the value
				// display sits just below the arc
				center: [ '50%', '52%' ],
				radius: '90%',
				axisLine: { lineStyle: { color: colorStops } },
				// scale the tick numbers and move them inward (toward the centre) so
				// they clear the tick marks; a larger distance sits further inward
				axisLabel: { fontSize: labelFsz, distance: labelFsz + 4 },
				// show the value below the dial, clear of the ticks, formatted
				detail: {
					offsetCenter: [ 0, '88%' ],
					fontSize: detailFsz,
					formatter: (v) => (fmtVal ? fmtVal(v) : String(v)) + units
				},
				data: [ { value: needle.value } ]
			} ]
		};
	}

	/**
	 * builds the ECharts option for a funnel chart
	 * <p>
	 * Each "entry" group is one funnel stage; the "sum" group is not part of the
	 * funnel. Within a group the x dimension selects the figure (x indexes xTitles).
	 * The segment width is driven by the dimension named in the metadata property
	 * "funnelBarValue" (falls back to "count"); the (money) "value" dimension is
	 * shown as the stage label, like the Windows client. A dynamic funnel lets the
	 * width follow that figure; a static funnel uses a fixed funnel shape (equal
	 * steps) regardless of the data.
	 * @param {Object} meta the chart meta data
	 * @param {Array} data the chart data points
	 * @param {String} locale the locale to use for number formatting
	 * @param {Boolean} dynamic <b>true</b> for a dynamic funnel (width follows the bar value),
	 *        <b>false</b> for a static funnel (fixed shape)
	 * @param {Number} width the available width in pixels, used to size the label gutters and for the small/big legend decision
	 * @param {Number} height the available height in pixels (used for the small/big legend decision); optional
	 * @returns {Object} the ECharts funnel option
	 */
	static _funnel(meta, data, locale, dynamic, width, height) {
		const props = meta.properties || {};
		const titles = props.groupTitles || [];
		const colors = meta.colors || [];
		const fmtVal = ChartOption._numberFormatter(props.yFormat, locale);
		const fmtCnt = ChartOption._numberFormatter(props.xFormat, locale);
		// x indexes xTitles (per spec). The server tells us, via the optional
		// "funnelBarValue" property, the name of the xTitles entry that drives the
		// segment width; it falls back to "count". The (money) "value" dimension is
		// shown as a label. Resolve the dimension indices by name.
		const xTitles = props.xTitles || [];
		const barName = props.funnelBarValue || 'count';
		const idxOf = (name, fallback) => {
			const idx = xTitles.indexOf(name);
			return idx >= 0 ? idx : fallback;
		};
		const idxBar = idxOf(barName, 2);
		const idxVal = idxOf('value', 0);
		const idxCnt = idxOf('count', 2);
		// collect the entry stages in server order; per stage keep the money value,
		// the count and the bar-width figure. The "sum" group is not a funnel
		// segment - it is kept aside and rendered as a summary row below the funnel.
		// group first, then classify sum-vs-entry from ANY of the group's points
		// (not just the first-seen one) - a stage carries several points (value/
		// count/bar dimensions) and the server is not guaranteed to emit the
		// "sum"-typed one first
		const byGroup = new Map();
		const groupOrder = [];
		data.forEach((point) => {
			const grp = point.group || 0;
			let stage = byGroup.get(grp);
			if ( !stage ) {
				// the backend does not yet send a stable element id in the chart
				// JSON; wired through already so a future server-side id needs no
				// further client change - see JsChart.js#_onChartClick
				stage = { group: grp, value: 0, count: 0, bar: 0, color: point.color, id: point.id, isSum: false };
				byGroup.set(grp, stage);
				groupOrder.push(grp);
			}
			// per the convention, a summary data point carries type "sum"; it is
			// kept aside and rendered as a bold summary row below the funnel rather
			// than as a funnel segment
			if ( point.type === 'sum' ) {
				stage.isSum = true;
			}
			const x = point.x || 0;
			// a non-numeric value must not poison the funnel's value domain (NaN
			// "bar" figures collapse/vanish every segment) or silently defeat the
			// isAllZero check below (NaN === 0 is false) - guard the same way the
			// gauge values are guarded, keeping the stage's initial 0 default
			if ( (x === idxVal) && Number.isFinite(point.value) ) {
				stage.value = point.value;
			}
			if ( (x === idxCnt) && Number.isFinite(point.value) ) {
				stage.count = point.value;
			}
			if ( (x === idxBar) && Number.isFinite(point.value) ) {
				stage.bar = point.value;
			}
		} );
		let sumStage = null;
		const stages = [];
		groupOrder.forEach((grp) => {
			const stage = byGroup.get(grp);
			if ( stage.isSum ) {
				sumStage = stage;
			}
			else {
				stages.push(stage);
			}
		} );
		// width follows the server-designated bar figure; a static funnel uses fixed
		// descending steps so the shape is independent of the data. An all-zero
		// dataset (every stage.bar === 0) intentionally still renders SOMETHING
		// (matching the app server's PNG renderer's own degenerate case) rather
		// than vanishing - do NOT special-case this into the static-style
		// fallback, an unresolved "funnelBarValue" dimension is indistinguishable
		// from genuinely all-zero real data at this point
		const values = stages.map((stage, idx) => dynamic ? stage.bar : (stages.length - idx));
		// ECharts' funnel layout maps ANY degenerate value domain (every value
		// equal - all-zero, or all-42, or anything else) to the MIDPOINT of
		// [minSize, maxSize], because it only sees a zero-width domain, not the
		// values' actual magnitude - it cannot tell "all zero" from "all equally
		// large" apart. With the default maxSize of "100%" that midpoint is 50%,
		// which is exactly right for "equal but clearly non-zero" data (e.g. every
		// stage genuinely worth 42) but far too fat for all-zero data, which should
		// look thin/near-empty. Narrow the size range ONLY when every value is
		// truly zero; any other degenerate-but-non-zero case keeps the ECharts
		// default (bigger bars, matching real, varying data). A single stage is
		// just as validly "all zero" as several, so no minimum stage count here.
		const isAllZero = (stages.length > 0) && values.every((v) => v === 0);
		const sizeRange = isAllZero ? { minSize: '4%', maxSize: '4%' } : {};
		// with sort:'none' ECharts draws segment i's bottom edge from segment
		// (i+1)'s value - the LAST segment has no "next" one, so ECharts defaults
		// that missing value to 0, tapering the last segment to a point regardless
		// of its own value. Fix: append an invisible phantom data point (same
		// value as the last real stage, so nothing tapers) purely to give the
		// last real stage something non-zero to interpolate its bottom edge
		// toward. ECharts distributes each item's VERTICAL slot evenly across
		// data.count(), so simply appending a phantom would shrink every real
		// segment to make room for it - give every item (real + phantom) an
		// explicit itemStyle.height instead, summing to 100%, with the phantom
		// getting 0% so it takes no visible vertical space.
		const heightPct = (100 / stages.length) + '%';
		const items = stages.map((stage, idx) => {
			return {
				id: stage.id,
				name: titles[stage.group] || '',
				value: values[idx],
				dspValue: stage.value,
				dspCount: stage.count,
				itemStyle: Object.assign( { height: heightPct }, stage.color != null && colors[stage.color] ? { color: colors[stage.color] } : undefined )
			};
		} );
		if ( items.length > 0 ) {
			const last = items[items.length - 1];
			items.push( {
				value: last.value,
				name: '',
				itemStyle: { height: '0%', opacity: 0 },
				label: { show: false },
				labelLine: { show: false },
				tooltip: { show: false },
				silent: true
			} );
		}
		// three columns like the Windows client: the stage NAME on the left, the
		// COUNT inside the segment (the bar figure), the (money) VALUE on the right.
		const nameFmt = (p) => (p.data || {}).name || '';
		const cntFmt = (p) => {
			const d = p.data || {};
			return Number.isFinite(d.dspCount) ? (fmtCnt ? fmtCnt(d.dspCount) : String(d.dspCount)) : '';
		};
		const valFmt = (p) => {
			const d = p.data || {};
			return Number.isFinite(d.dspValue) && fmtVal ? fmtVal(d.dspValue) : '';
		};
		// tooltip carries all three figures
		const tipFmt = (p) => {
			const val = valFmt(p);
			const cnt = cntFmt(p);
			let s = p.name || '';
			if ( val.length > 0 ) { s += ': ' + val; }
			if ( cnt.length > 0 ) { s += ' (' + cnt + ')'; }
			return s;
		};
		// size the left/right gutters to the longest label so nothing is clipped;
		// the funnel itself fills whatever is left. Pixel gutters (not percentages)
		// keep the label columns constant when the chart is expanded or resized.
		const font = '12px sans-serif';
		const pad = 16;
		const nameW = Math.ceil(Math.max(0, ...items.map((it) => ChartOption._textWidth(it.name, font)))) + pad;
		const valW = Math.ceil(Math.max(0, ...items.map((it) => ChartOption._textWidth(valFmt({ data: it }), font)))) + pad;
		// clamp the gutters so the funnel keeps a minimum width in a very small tile
		let leftGut = nameW;
		let rightGut = valW;
		if ( width > 0 ) {
			const maxGut = Math.max(0, (width - 60) / 2);	// leave >= 60px for the funnel
			leftGut = Math.min(leftGut, maxGut);
			rightGut = Math.min(rightGut, maxGut);
		}
		// reserve a bottom band for the summary row (if present); both series share
		// the same box so their geometry (and thus the overlaid labels) line up
		const sumH = sumStage ? 16 : 0;
		const funnelBox = Object.assign( { left: leftGut, right: rightGut, top: 10, bottom: 4 + sumH }, sizeRange );
		const option = {
			title: ChartOption._noDataTitle(meta, data),
			tooltip: { trigger: 'item', confine: true, formatter: tipFmt },
			legend: ChartOption._legend(meta, width, height, items.map((it) => it.name)),
			series: [
				Object.assign( {
					type: 'funnel',
					sort: 'none',
					// count centered inside the segment
					label: { show: true, position: 'inside', formatter: cntFmt },
					data: items
				}, funnelBox ),
				Object.assign( {
					type: 'funnel',
					sort: 'none',
					// same geometry, no fill/tooltip - only the outside labels. Use a
					// transparent fill (not itemStyle.opacity, which would also fade the
					// label text) so the segments are invisible but the labels stay opaque
					itemStyle: { color: 'transparent', borderWidth: 0 },
					silent: true,
					tooltip: { show: false },
					// the stage name on the left
					label: { show: true, position: 'left', color: '#000', formatter: nameFmt },
					labelLine: { show: true, length: 8 },
					data: items
				}, funnelBox ),
				Object.assign( {
					type: 'funnel',
					sort: 'none',
					itemStyle: { color: 'transparent', borderWidth: 0 },
					silent: true,
					tooltip: { show: false },
					// the (money) value on the right
					label: { show: true, position: 'right', color: '#000', formatter: valFmt },
					labelLine: { show: true, length: 8 },
					data: items
				}, funnelBox )
			]
		};
		// summary row directly below the funnel, bold, aligned to the same three
		// columns; the count is centered under the funnel's lower tip (the funnel is
		// centered within its box, whose center is offset from the chart center
		// because the left/right gutters differ)
		if ( sumStage ) {
			const bold = { fontWeight: 'bold', fill: '#000', fontFamily: 'sans-serif', fontSize: 12 };
			// reuse cntFmt/valFmt (rather than re-encoding the same formatting rule)
			// by wrapping the sum stage's figures the same way ECharts data items are
			// shaped for those formatters
			const sumData = { dspCount: sumStage.count, dspValue: sumStage.value };
			const cnt = cntFmt( { data: sumData } );
			const val = valFmt( { data: sumData } );
			const cntPos = width > 0 ? { left: leftGut + (width - leftGut - rightGut) / 2 } : { left: 'center' };
			option.graphic = [
				{ type: 'text', left: 5, bottom: 3, style: Object.assign({ text: titles[sumStage.group] || '', align: 'left' }, bold) },
				Object.assign({ type: 'text', bottom: 3, style: Object.assign({ text: cnt, align: 'center' }, bold) }, cntPos),
				{ type: 'text', right: 5, bottom: 3, style: Object.assign({ text: val, align: 'right' }, bold) }
			];
		}
		return option;
	}
}
